/**
 * Sistema de Proteção para Produtos Digitais
 * Garante que produtos digitais (games, dlc, gift cards) nunca sejam adicionados com quantidade > 1
 * 
 * Funcionalidades:
 * - Intercepta todas as requisições para /cart/add.js
 * - Debounce nos botões de adicionar ao carrinho
 * - Validação client-side antes de adicionar
 * - Override automático de quantidade para produtos digitais
 * - Prevenção de cliques múltiplos
 */

class DigitalProductsProtection {
  constructor() {
    this.digitalProductTypes = [
      'game', 'games', 'jogo', 'jogos',
      'dlc', 'dlcs', 'downloadable content',
      'gift card', 'gift-card', 'giftcard', 'gift', 'cartão presente',
      'edition', 'editions', 'edição', 'edições',
      'top up', 'top-up', 'topup', 'recarga'
    ];
    
    this.addToCartInProgress = new Set(); // Track products being added
    this.debounceTimers = new Map(); // Debounce timers per product
    
    // Carregar keywords de títulos das configurações do tema
    this.titleKeywords = this.loadTitleKeywordsFromSettings();
    
    // 🚀 OTIMIZAÇÕES DE PERFORMANCE
    this.productInfoCache = new Map(); // Cache de informações de produtos
    this.keywordInProgress = new Set(); // Track keywords sendo adicionadas (bloqueio instantâneo)
    this.lastCartCheck = { timestamp: 0, data: null }; // Cache do carrinho
    this.CART_CACHE_DURATION = 1000; // 1 segundo de cache
    
    this.init();
  }

  /**
   * Carrega as palavras-chave de título das configurações do tema
   */
  loadTitleKeywordsFromSettings() {
    try {
      // Tentar obter do window.Theme.settings
      const settingsText = window.Theme?.settings?.digital_products_title_keywords || '';
      
      if (!settingsText) {
        console.log('[DigitalProductsProtection] ⚠️ Nenhuma keyword de título configurada');
        return [];
      }
      
      // Separar por linha e limpar
      const keywords = settingsText
        .split('\n')
        .map(k => k.trim())
        .filter(k => k.length > 0)
        .map(k => k.toLowerCase()); // Normalizar para lowercase
      
      console.log('[DigitalProductsProtection] 📋 Keywords de título carregadas:', keywords);
      return keywords;
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao carregar keywords:', error);
      return [];
    }
  }

  init() {
    this.interceptFetch();
    this.setupButtonProtection();
    this.setupFormProtection();
    this.setupCartCorrection();
    this.updateAddToCartButtons();
    
    // Atualizar botões quando carrinho mudar (evento correto: cart:update)
    document.addEventListener('cart:update', () => {
      console.log('[DigitalProductsProtection] 🔄 Evento cart:update recebido, atualizando botões');
      this.updateAddToCartButtons();
    });
    
    console.log('[DigitalProductsProtection] Sistema de proteção ativado');
  }

  /**
   * Intercepta todas as requisições fetch para /cart/add.js
   */
  interceptFetch() {
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(url, options = {}) {
      // Intercepta apenas requisições para adicionar ao carrinho
      if (url.includes('/cart/add.js') || url.includes('cart/add')) {
        console.log('[DigitalProductsProtection] 🎯 Interceptando requisição para carrinho:', url);
        
        return self.validateAndModifyCartRequest(url, options, originalFetch);
      }
      
      return originalFetch.apply(this, arguments);
    };
  }

  /**
   * Valida e modifica a requisição de adicionar ao carrinho
   */
  async validateAndModifyCartRequest(url, options, originalFetch) {
    try {
      // Extrair dados do FormData ou JSON
      let productData = await this.extractProductData(options.body);
      
      // 🚀 Se não conseguiu extrair dados, deixar passar SEM BLOQUEIO
      if (!productData) {
        console.log('[DigitalProductsProtection] ⚠️ Não foi possível extrair dados, permitindo requisição');
        return originalFetch.apply(window, [url, options]);
      }
      
      const { variantId, quantity, productInfo } = productData;
      
      console.log('[DigitalProductsProtection] 📦 Processando adição:', {
        variantId: variantId,
        quantity: quantity,
        title: productInfo?.title
      });
      
      // 🚀 VERIFICAÇÃO RÁPIDA: Se não tem productInfo, deixar passar
      if (!productInfo) {
        console.log('[DigitalProductsProtection] ⚡ Sem informações do produto, permitindo requisição');
        return originalFetch.apply(window, [url, options]);
      }
        
      // 1️⃣ VERIFICAÇÃO POR TÍTULO (PRIORIDADE)
      if (productInfo.title) {
        const titleKeyword = this.hasTitleKeyword(productInfo.title);
          
          if (titleKeyword) {
            console.log('[DigitalProductsProtection] 🔑 Produto limitado por título detectado:', titleKeyword);
            
            // Verificar se já existe produto com essa keyword no carrinho
            const cartCheck = await this.hasProductWithTitleKeywordInCart(titleKeyword);
            
            if (cartCheck.found) {
              console.log('[DigitalProductsProtection] ❌ Produto com título similar já no carrinho, BLOQUEANDO');
              
              // Mostrar mensagem de erro personalizada
              this.showDigitalProductError(
                `Você já tem "${cartCheck.existingProduct}" no carrinho. Apenas 1 produto com "${titleKeyword}" é permitido.`
              );
              
              // Atualizar botões imediatamente
              setTimeout(() => this.updateAddToCartButtons(), 100);
              
              // Limpar estado de "em progresso"
              this.addToCartInProgress.delete(variantId);
              
              // Retornar resposta de erro
              return new Response(
                JSON.stringify({ 
                  error: true,
                  message: `Você já tem um produto "${titleKeyword}" no carrinho.`,
                  blocked: true,
                  reason: 'title_keyword_limit'
                }),
                { 
                  status: 422,
                  headers: { 'Content-Type': 'application/json' } 
                }
              );
            }
            
            // Forçar quantidade = 1 para produtos limitados por título
            if (quantity > 1) {
              console.log(`[DigitalProductsProtection] [TÍTULO] Forçando quantidade de ${quantity} para 1`);
              options.body = this.modifyRequestBody(options.body, { quantity: 1 });
            }
          }
        }
        
        // 2️⃣ VERIFICAÇÃO POR TIPO (PRODUTO DIGITAL)
        const isDigital = await this.isDigitalProduct(variantId, productInfo);
        
        if (isDigital) {
          console.log('[DigitalProductsProtection] 🎮 Produto digital detectado:', productInfo?.title);
          
          // Verificar se já está no carrinho
          const currentCartQty = await this.getProductQuantityInCart(variantId);
          
          if (currentCartQty > 0) {
            console.log('[DigitalProductsProtection] ❌ Produto digital já está no carrinho, BLOQUEANDO adição');
            
            // Mostrar mensagem de erro
            this.showDigitalProductError('Este produto digital já está no seu carrinho.');
            
            // Atualizar botões imediatamente
            setTimeout(() => this.updateAddToCartButtons(), 100);
            
            // Limpar estado de "em progresso"
            this.addToCartInProgress.delete(variantId);
            
            // Retornar resposta de erro para não adicionar ao carrinho
            return new Response(
              JSON.stringify({ 
                error: true,
                message: 'Este produto digital já está no seu carrinho.',
                blocked: true,
                reason: 'digital_product_limit'
              }),
              { 
                status: 422,
                headers: { 'Content-Type': 'application/json' } 
              }
            );
          }
          
          // Forçar quantidade = 1 para produtos digitais
          if (quantity > 1) {
            console.log(`[DigitalProductsProtection] [DIGITAL] Forçando quantidade de ${quantity} para 1`);
            options.body = this.modifyRequestBody(options.body, { quantity: 1 });
          }
        }
      
      // Executar requisição original (modificada se necessário)
      const response = await originalFetch.apply(window, [url, options]);
      
      // Se a adição foi bem-sucedida, limpar o estado de "em progresso" e atualizar botões
      if (response.ok && productData) {
        this.addToCartInProgress.delete(productData.variantId);
        
        // 🚀 LIMPAR CACHE DO CARRINHO (item foi adicionado)
        this.lastCartCheck = { timestamp: 0, data: null };
        
        // Liberar keyword se existir
        if (productData.productInfo && productData.productInfo.title) {
          const keyword = this.hasTitleKeyword(productData.productInfo.title);
          if (keyword) {
            this.keywordInProgress.delete(keyword);
            console.log('[DigitalProductsProtection] 🔓 Liberando keyword após sucesso:', keyword);
          }
        }
        
        console.log('[DigitalProductsProtection] ✅ Produto adicionado com sucesso, atualizando botões');
        
        // Atualizar botões após adicionar ao carrinho (sem disparar evento)
        setTimeout(() => {
          this.updateAddToCartButtons();
        }, 200);
      }
      
      return response;
      
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao processar requisição:', error);
      return originalFetch.apply(window, [url, options]);
    }
  }

  /**
   * Extrai dados do produto da requisição (com timeout)
   */
  async extractProductData(body) {
    try {
      let variantId, quantity;
      
      if (body instanceof FormData) {
        variantId = body.get('id');
        quantity = parseInt(body.get('quantity') || '1');
      } else if (typeof body === 'string') {
        // Parse JSON body se necessário
        const data = JSON.parse(body);
        variantId = data.id;
        quantity = parseInt(data.quantity || '1');
      }
      
      if (variantId) {
        console.log('[DigitalProductsProtection] 📝 Extraindo dados para variant:', variantId);
        
        // 🚀 TIMEOUT de 3 segundos para busca de produto
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout ao buscar produto')), 3000)
        );
        
        try {
          // Buscar informações do produto com timeout
          const productInfo = await Promise.race([
            this.getProductInfoByVariant(variantId),
            timeoutPromise
          ]);
          
          console.log('[DigitalProductsProtection] ✅ Dados extraídos com sucesso');
          
          return { variantId, quantity, productInfo };
        } catch (timeoutError) {
          console.warn('[DigitalProductsProtection] ⏱️ Timeout ao buscar produto, permitindo requisição:', timeoutError.message);
          
          // Retornar dados básicos sem productInfo
          return { variantId, quantity, productInfo: null };
        }
      }
    } catch (error) {
      console.error('[DigitalProductsProtection] ❌ Erro ao extrair dados:', error);
    }
    
    return null;
  }

  /**
   * Verifica se um produto tem keyword de título limitada
   */
  hasTitleKeyword(productTitle) {
    if (!productTitle || this.titleKeywords.length === 0) {
      return null;
    }
    
    const titleLower = productTitle.toLowerCase();
    
    // Procurar se alguma keyword está no título
    for (const keyword of this.titleKeywords) {
      if (titleLower.includes(keyword)) {
        console.log('[DigitalProductsProtection] 🔑 Keyword encontrada no título:', keyword, '→', productTitle);
        return keyword;
      }
    }
    
    return null;
  }
  
  /**
   * Verifica se já existe um produto com a mesma keyword de título no carrinho (com cache)
   */
  async hasProductWithTitleKeywordInCart(keyword) {
    try {
      // 🚀 VERIFICAR CACHE DO CARRINHO
      const now = Date.now();
      let cart;
      
      if (this.lastCartCheck.data && (now - this.lastCartCheck.timestamp) < this.CART_CACHE_DURATION) {
        console.log('[DigitalProductsProtection] ⚡ Usando cache do carrinho');
        cart = this.lastCartCheck.data;
      } else {
        const response = await fetch('/cart.js');
        cart = await response.json();
        // 🚀 SALVAR NO CACHE
        this.lastCartCheck = {
          timestamp: now,
          data: cart
        };
      }
      
      console.log('[DigitalProductsProtection] 🔍 Verificando carrinho para keyword:', keyword);
      
      for (const item of cart.items) {
        const itemTitle = item.product_title || item.title || '';
        const itemKeyword = this.hasTitleKeyword(itemTitle);
        
        if (itemKeyword === keyword) {
          console.log('[DigitalProductsProtection] ✅ Produto com keyword já no carrinho:', itemTitle);
          return {
            found: true,
            existingProduct: itemTitle
          };
        }
      }
      
      console.log('[DigitalProductsProtection] ❌ Nenhum produto com keyword no carrinho');
      return { found: false };
      
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao verificar carrinho:', error);
      return { found: false };
    }
  }

  /**
   * Verifica se um produto é digital baseado no variant ID
   */
  async isDigitalProduct(variantId, productInfo = null) {
    try {
      if (!productInfo) {
        productInfo = await this.getProductInfoByVariant(variantId);
      }
      
      if (!productInfo) {
        console.warn('[DigitalProductsProtection] ⚠️ Produto não encontrado para variant:', variantId);
        return false;
      }
      
      const { productType, tags, title } = productInfo;
      
      console.log('[DigitalProductsProtection] 🔍 Verificando produto:', {
        title: title,
        productType: productType,
        productTypeRaw: productType,
        tags: tags
      });
      
      // VERIFICAÇÃO ESPECIAL PARA TOP UP (antes da normalização)
      const productTypeLower = productType ? productType.toLowerCase() : '';
      if (productTypeLower.includes('top up') || productTypeLower.includes('top-up') || productTypeLower.includes('topup')) {
        console.log('[DigitalProductsProtection] ✅ MATCH ESPECIAL TOP UP detectado:', productType);
        return true;
      }
      
      // Verificar tags diretamente para "top up" (antes da normalização)
      if (tags && Array.isArray(tags)) {
        for (const tag of tags) {
          const tagLower = tag.toLowerCase();
          if (tagLower.includes('top up') || tagLower.includes('top-up') || tagLower.includes('topup') || tagLower.includes('recarga')) {
            console.log('[DigitalProductsProtection] ✅ MATCH ESPECIAL TOP UP por tag:', tag);
            return true;
          }
        }
      }
      
      // Normalizar product_type (remover espaços, hífens, underscores)
      const productTypeNormalized = productType ? productType.toLowerCase().replace(/[\s\-_]/g, '') : '';
      
      console.log('[DigitalProductsProtection] 📝 Product Type Normalizado:', productTypeNormalized);
      
      // Verificar product_type (normalizado)
      const typeIsDigital = this.digitalProductTypes.some(type => {
        const typeNormalized = type.toLowerCase().replace(/[\s\-_]/g, '');
        const isMatch = productTypeNormalized.includes(typeNormalized);
        
        if (isMatch) {
          console.log('[DigitalProductsProtection] ✅ MATCH por tipo normalizado:', type, '→', productType);
        }
        
        return isMatch;
      });
      
      // Verificar tags (normalizado)
      const tagsAreDigital = tags && tags.some(tag => {
        const tagNormalized = tag.toLowerCase().replace(/[\s\-_]/g, '');
        
        return this.digitalProductTypes.some(type => {
          const typeNormalized = type.toLowerCase().replace(/[\s\-_]/g, '');
          const isMatch = tagNormalized.includes(typeNormalized);
          
          if (isMatch) {
            console.log('[DigitalProductsProtection] ✅ MATCH por tag normalizada:', type, '→', tag);
          }
          
          return isMatch;
        });
      });
      
      const isDigital = typeIsDigital || tagsAreDigital;
      
      console.log('[DigitalProductsProtection] 🎯 Resultado final:', isDigital ? '✅ É DIGITAL' : '❌ NÃO é digital');
      
      return isDigital;
      
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao verificar produto digital:', error);
      return false;
    }
  }

  /**
   * Obtém informações do produto via Shopify API (com cache)
   */
  async getProductInfoByVariant(variantId) {
    try {
      // 🚀 VERIFICAR CACHE PRIMEIRO
      if (this.productInfoCache.has(variantId)) {
        console.log('[DigitalProductsProtection] ⚡ Usando cache para variant:', variantId);
        return this.productInfoCache.get(variantId);
      }
      
      console.log('[DigitalProductsProtection] 🔎 Buscando informações do variant:', variantId);
      
      // Primeiro tentar buscar pelo DOM (mais rápido e confiável)
      const productInfo = this.getProductInfoFromDOM(variantId);
      if (productInfo) {
        console.log('[DigitalProductsProtection] ✅ Produto encontrado no DOM:', productInfo);
        // 🚀 SALVAR NO CACHE
        this.productInfoCache.set(variantId, productInfo);
        return productInfo;
      }
      
      // Fallback: buscar via API
      console.log('[DigitalProductsProtection] 🌐 Buscando produto via API /products.json');
      const response = await fetch(`/products.json?limit=250`);
      const data = await response.json();
      
      console.log('[DigitalProductsProtection] 📦 Total de produtos na resposta:', data.products.length);
      
      for (const product of data.products) {
        const variant = product.variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          console.log('[DigitalProductsProtection] ✅ Produto encontrado na API:', product.title);
          const info = {
            productType: product.product_type,
            tags: product.tags,
            title: product.title,
            handle: product.handle
          };
          // 🚀 SALVAR NO CACHE
          this.productInfoCache.set(variantId, info);
          return info;
        }
      }
      
      console.warn('[DigitalProductsProtection] ⚠️ Produto NÃO encontrado para variant:', variantId);
      return null;
    } catch (error) {
      console.error('[DigitalProductsProtection] ❌ Erro ao buscar produto:', error);
      return null;
    }
  }
  
  /**
   * Tenta obter informações do produto a partir do DOM
   */
  getProductInfoFromDOM(variantId) {
    try {
      // Buscar o formulário que contém este variant
      const variantInput = document.querySelector(`input[name="id"][value="${variantId}"], select[name="id"] option[value="${variantId}"]`);
      
      if (!variantInput) {
        return null;
      }
      
      const form = variantInput.closest('form');
      if (!form) {
        return null;
      }
      
      // Buscar inputs hidden com data attributes dentro do formulário
      const productTypeInput = form.querySelector('input[data-product-type]');
      const productTagsInput = form.querySelector('input[data-product-tags]');
      const productTitleInput = form.querySelector('input[data-product-title]');
      
      if (productTypeInput || productTagsInput || productTitleInput) {
        const productType = productTypeInput ? productTypeInput.dataset.productType : '';
        const tagsString = productTagsInput ? productTagsInput.dataset.productTags : '';
        const tags = tagsString ? tagsString.split(',').map(t => t.trim()) : [];
        const title = productTitleInput ? productTitleInput.dataset.productTitle : '';
        
        if (productType || tags.length > 0 || title) {
          console.log('[DigitalProductsProtection] 📋 Informações extraídas do DOM:', {
            productType,
            tags,
            title
          });
          
          return {
            productType: productType,
            tags: tags,
            title: title,
            handle: ''
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao extrair info do DOM:', error);
      return null;
    }
  }

  /**
   * Verifica quantidade atual do produto no carrinho
   */
  async getProductQuantityInCart(variantId) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const item = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      const quantity = item ? item.quantity : 0;
      
      console.log('[DigitalProductsProtection] 📊 Quantidade no carrinho do variant', variantId, ':', quantity);
      
      return quantity;
      
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao verificar carrinho:', error);
      return 0;
    }
  }

  /**
   * Modifica o body da requisição para alterar a quantidade
   */
  modifyRequestBody(body, changes) {
    if (body instanceof FormData) {
      // Clonar FormData e aplicar mudanças
      const newFormData = new FormData();
      
      for (const [key, value] of body.entries()) {
        if (changes[key] !== undefined) {
          newFormData.append(key, changes[key]);
        } else {
          newFormData.append(key, value);
        }
      }
      
      return newFormData;
    } else if (typeof body === 'string') {
      // Modificar JSON
      const data = JSON.parse(body);
      Object.assign(data, changes);
      return JSON.stringify(data);
    }
    
    return body;
  }

  /**
   * Configura proteção nos botões de adicionar ao carrinho
   */
  setupButtonProtection() {
    // Usar event delegation para capturar todos os botões
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button[name="add"], button[ref="addToCartButton"]');
      
      if (button) {
        this.handleAddToCartClick(event, button);
      }
    });
  }

  /**
   * Manipula clique no botão de adicionar ao carrinho (OTIMIZADO)
   */
  async handleAddToCartClick(event, button) {
    const form = button.closest('form');
    if (!form) return;
    
    const variantId = form.querySelector('select[name="id"], input[name="id"]')?.value;
    if (!variantId) return;
    
    // 🚀 VERIFICAÇÃO RÁPIDA: Obter info do produto PRIMEIRO
    const productInfo = await this.getProductInfoByVariant(variantId);
    
    if (!productInfo) {
      console.log('[DigitalProductsProtection] ⚡ Sem info do produto, permitindo adição normal');
      return; // Deixar o formulário ser submetido normalmente
    }
    
    // Verificar se precisa aplicar proteção
    const titleKeyword = productInfo.title ? this.hasTitleKeyword(productInfo.title) : null;
    const isDigital = await this.isDigitalProduct(variantId, productInfo);
    
    // 🚀 SE NÃO TEM KEYWORD E NÃO É DIGITAL = PRODUTO NORMAL → LIBERAR
    if (!titleKeyword && !isDigital) {
      console.log('[DigitalProductsProtection] ✅ Produto normal, permitindo adição sem restrições');
      return; // Deixar o formulário ser submetido normalmente
    }
    
    // 🔒 PRODUTO PRECISA DE PROTEÇÃO - APLICAR BLOQUEIOS
    console.log('[DigitalProductsProtection] 🛡️ Produto requer proteção, aplicando verificações...');
    
    // 🚀 BLOQUEIO INSTANTÂNEO POR VARIANT
    if (this.addToCartInProgress.has(variantId)) {
      console.log('[DigitalProductsProtection] 🚫 Adição já em progresso (variant), BLOQUEANDO instantaneamente');
      event.preventDefault();
      event.stopPropagation();
      this.showDigitalProductError('Aguarde um momento antes de adicionar novamente.');
      return;
    }
    
    // Marcar variant como em progresso
    this.addToCartInProgress.add(variantId);
    
    // 1️⃣ BLOQUEIO INSTANTÂNEO POR KEYWORD
    
    if (titleKeyword) {
      // 🚀 VERIFICAR SE KEYWORD JÁ ESTÁ SENDO ADICIONADA
      if (this.keywordInProgress.has(titleKeyword)) {
        console.log('[DigitalProductsProtection] ⚡ Keyword já em progresso, BLOQUEANDO INSTANTANEAMENTE:', titleKeyword);
        event.preventDefault();
        event.stopPropagation();
        this.showDigitalProductError(`Aguarde! Um produto "${titleKeyword}" já está sendo adicionado.`);
        this.addToCartInProgress.delete(variantId);
        return;
      }
      
      // 🚀 MARCAR KEYWORD COMO EM PROGRESSO (bloqueio global por keyword)
      this.keywordInProgress.add(titleKeyword);
      console.log('[DigitalProductsProtection] 🔒 Bloqueando TODOS os produtos com keyword:', titleKeyword);
      
      // Verificar se já existe no carrinho
      const cartCheck = await this.hasProductWithTitleKeywordInCart(titleKeyword);
      
      if (cartCheck.found) {
        console.log('[DigitalProductsProtection] ❌ Produto com título similar já no carrinho');
        event.preventDefault();
        event.stopPropagation();
        this.showDigitalProductError(
          `Você já tem "${cartCheck.existingProduct}" no carrinho. Apenas 1 produto com "${titleKeyword}" é permitido.`
        );
        
        // Liberar bloqueios
        this.keywordInProgress.delete(titleKeyword);
        this.addToCartInProgress.delete(variantId);
        return;
      }
      
      // Forçar quantidade = 1
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput && parseInt(quantityInput.value) > 1) {
        console.log('[DigitalProductsProtection] [TÍTULO] Forçando quantidade para 1');
        quantityInput.value = '1';
      }
      
      // 🚀 LIBERAR KEYWORD APÓS 3 SEGUNDOS (tempo para requisição completar)
      setTimeout(() => {
        this.keywordInProgress.delete(titleKeyword);
        console.log('[DigitalProductsProtection] 🔓 Liberando keyword:', titleKeyword);
      }, 3000);
    }
    
    // 2️⃣ VERIFICAR SE É PRODUTO DIGITAL (já verificado no início)
    if (isDigital) {
      // Verificar se já está no carrinho
      const currentQty = await this.getProductQuantityInCart(variantId);
      if (currentQty > 0) {
        console.log('[DigitalProductsProtection] ❌ Produto digital já no carrinho');
        event.preventDefault();
        event.stopPropagation();
        this.showDigitalProductError('Este produto digital já está no seu carrinho.');
        
        // Liberar bloqueios
        if (titleKeyword) this.keywordInProgress.delete(titleKeyword);
        this.addToCartInProgress.delete(variantId);
        return;
      }
      
      // Forçar quantidade = 1
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput && parseInt(quantityInput.value) > 1) {
        console.log('[DigitalProductsProtection] [DIGITAL] Forçando quantidade para 1');
        quantityInput.value = '1';
      }
    }
    
    // 🚀 LIBERAR VARIANT APÓS 5 SEGUNDOS
    setTimeout(() => {
      this.addToCartInProgress.delete(variantId);
      console.log('[DigitalProductsProtection] 🔓 Liberando variant:', variantId);
    }, 5000);
    
    // Configurar debounce visual
    this.setupDebounce(variantId, button);
  }

  /**
   * Configura debounce para um botão específico
   */
  setupDebounce(variantId, button) {
    // Limpar timer anterior se existir
    if (this.debounceTimers.has(variantId)) {
      clearTimeout(this.debounceTimers.get(variantId));
    }
    
    // Desabilitar botão temporariamente
    button.disabled = true;
    button.style.opacity = '0.7';
    
    // Reabilitar após 3 segundos
    const timer = setTimeout(() => {
      button.disabled = false;
      button.style.opacity = '1';
      this.addToCartInProgress.delete(variantId);
      this.debounceTimers.delete(variantId);
    }, 3000);
    
    this.debounceTimers.set(variantId, timer);
  }

  /**
   * Configura proteção nos formulários
   */
  setupFormProtection() {
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      
      if (form.action && form.action.includes('/cart/add')) {
        await this.handleFormSubmit(event, form);
      }
    });
  }

  /**
   * Manipula submissão de formulário
   */
  async handleFormSubmit(event, form) {
    const variantId = form.querySelector('select[name="id"], input[name="id"]')?.value;
    const quantityInput = form.querySelector('input[name="quantity"]');
    
    if (!variantId) return;
    
    const isDigital = await this.isDigitalProduct(variantId);
    if (!isDigital) return;
    
    // Forçar quantidade = 1 para produtos digitais
    if (quantityInput && parseInt(quantityInput.value) > 1) {
      console.log('[DigitalProductsProtection] Forçando quantidade para 1 no formulário');
      quantityInput.value = '1';
    }
  }

  /**
   * Configura correção automática do carrinho
   */
  setupCartCorrection() {
    let isCorrectingCart = false;
    let correctionTimeout = null;
    
    // Monitorar mudanças no carrinho e corrigir quantidades (evento correto: cart:update)
    document.addEventListener('cart:update', (event) => {
      // Ignorar eventos de correção para evitar loop infinito
      if (event.detail?.source === 'digital-protection-correction') {
        console.log('[DigitalProductsProtection] 🔄 Ignorando evento de correção própria');
        return;
      }
      
      console.log('[DigitalProductsProtection] 🔄 Evento cart:update recebido em setupCartCorrection');
      
      // Limpar timeout anterior
      if (correctionTimeout) {
        clearTimeout(correctionTimeout);
      }
      
      // Debounce correções
      correctionTimeout = setTimeout(() => {
        if (!isCorrectingCart) {
          isCorrectingCart = true;
          this.correctCartQuantities().finally(() => {
            isCorrectingCart = false;
          });
        }
      }, 1000); // Aumentar debounce para 1 segundo
    });
    
    // Executar correção inicial apenas uma vez
    setTimeout(() => {
      this.correctCartQuantities();
      this.updateAddToCartButtons();
    }, 2000); // Aumentar delay inicial
  }

  /**
   * Corrige quantidades de produtos digitais no carrinho
   */
  async correctCartQuantities() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log('[DigitalProductsProtection] 🛒 Verificando carrinho:', cart.items.length, 'items');
      
      let needsCorrection = false;
      let correctionsMade = 0;
      
      for (const item of cart.items) {
        const isDigital = await this.isDigitalProduct(item.variant_id, {
          productType: item.product_type,
          tags: item.tags ? item.tags.split(', ') : [],
          title: item.product_title
        });
        
        if (isDigital && item.quantity > 1) {
          console.log('[DigitalProductsProtection] 🔧 Corrigindo quantidade de:', item.product_title, 'de', item.quantity, 'para 1');
          
          needsCorrection = true;
          correctionsMade++;
          
          // Corrigir quantidade para 1
          await this.updateCartItemQuantity(item.key, 1);
          
          // Pequeno delay entre correções
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Se houve correção, recarregar interface
      if (needsCorrection) {
        console.log('[DigitalProductsProtection] 🔄', correctionsMade, 'correções aplicadas, recarregando interface');
        
        // Forçar atualização visual do carrinho
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents();
        }
        
        // Atualizar contador do carrinho se existir
        const cartCount = document.querySelector('.cart-count, .cart-count-bubble');
        if (cartCount && cart.items) {
          cartCount.textContent = cart.items.length;
        }
      }
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao corrigir carrinho:', error);
    }
  }

  /**
   * Atualiza quantidade de um item específico no carrinho
   */
  async updateCartItemQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      
      if (response.ok) {
        console.log('[DigitalProductsProtection] ✅ Quantidade corrigida para:', quantity);
        
        // 🚀 LIMPAR CACHE DO CARRINHO (quantidade foi alterada)
        this.lastCartCheck = { timestamp: 0, data: null };
        
        // Recarregar carrinho com identificador para evitar loop (evento correto: cart:update)
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { source: 'digital-protection-correction' }
        }));
      }
    } catch (error) {
      console.error('[DigitalProductsProtection] Erro ao atualizar quantidade:', error);
    }
  }

  /**
   * Atualiza botões de adicionar ao carrinho para produtos digitais já no carrinho
   */
  async updateAddToCartButtons() {
    try {
      console.log('[DigitalProductsProtection] 🔄 Atualizando estado dos botões');
      
      // Buscar itens atuais do carrinho
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Criar set de variant IDs no carrinho
      const variantIdsInCart = new Set(cart.items.map(item => item.variant_id.toString()));
      
      // Criar set de keywords de título já no carrinho
      const titleKeywordsInCart = new Set();
      cart.items.forEach(item => {
        const title = item.product_title || item.title || '';
        const keyword = this.hasTitleKeyword(title);
        if (keyword) {
          titleKeywordsInCart.add(keyword);
        }
      });
      
      console.log('[DigitalProductsProtection] 🛒 Produtos no carrinho:', variantIdsInCart.size);
      console.log('[DigitalProductsProtection] 🔑 Keywords de título no carrinho:', Array.from(titleKeywordsInCart));
      
      // Encontrar todos os botões de adicionar ao carrinho (incluindo quick add)
      const addToCartButtons = document.querySelectorAll('button[name="add"], button[type="submit"][form*="product-form"], .product-form__submit, .jv-quick-add-button');
      
      console.log('[DigitalProductsProtection] 🔍 Total de botões encontrados:', addToCartButtons.length);
      
      for (const button of addToCartButtons) {
        const form = button.closest('form');
        if (!form) continue;
        
        const variantInput = form.querySelector('select[name="id"], input[name="id"]');
        if (!variantInput) continue;
        
        const variantId = variantInput.value;
        if (!variantId) continue;
        
        // Obter informações do produto
        const productInfo = await this.getProductInfoByVariant(variantId);
        if (!productInfo) continue;
        
        let shouldDisable = false;
        let disableReason = '';
        
        // 1️⃣ VERIFICAR SE TEM KEYWORD DE TÍTULO NO CARRINHO
        if (productInfo.title) {
          const titleKeyword = this.hasTitleKeyword(productInfo.title);
          
          if (titleKeyword && titleKeywordsInCart.has(titleKeyword)) {
            shouldDisable = true;
            disableReason = `Produto com "${titleKeyword}" já no carrinho`;
            console.log(`[DigitalProductsProtection] 🔑 Produto ${productInfo.title} bloqueado por título:`, titleKeyword);
          }
        }
        
        // 2️⃣ VERIFICAR SE É PRODUTO DIGITAL NO CARRINHO
        const isDigital = await this.isDigitalProduct(variantId, productInfo);
        const isInCart = variantIdsInCart.has(variantId.toString());
        
        if (isDigital && isInCart) {
          shouldDisable = true;
          disableReason = 'Produto digital já no carrinho';
        }
        
        // Aplicar ou remover desabilitação
        if (shouldDisable) {
          console.log(`[DigitalProductsProtection] ✅ Desabilitando botão: ${disableReason}`);
          
          // Verificar se é botão quick-add (só tem ícone, sem texto)
          const isQuickAddButton = button.classList.contains('jv-quick-add-button');
          
          if (isQuickAddButton) {
            // Quick Add: Apenas desabilitar, NÃO alterar conteúdo (mantém ícone)
            button.disabled = true;
            button.classList.add('digital-product-in-cart');
          } else {
            // Botão normal: Salvar HTML e alterar texto
            if (!button.dataset.originalHTML) {
              button.dataset.originalHTML = button.innerHTML;
            }
            
            // Atualizar texto do botão
            button.innerHTML = 'Adicionado ao carrinho';
            button.disabled = true;
            button.style.opacity = '0.7';
            button.style.cursor = 'not-allowed';
            button.classList.add('digital-product-in-cart');
          }
          
        } else if (button.classList.contains('digital-product-in-cart')) {
          // Produto NÃO está mais bloqueado - reativar botão
          console.log('[DigitalProductsProtection] 🔓 Produto removido do carrinho, reativando botão');
          
          const isQuickAddButton = button.classList.contains('jv-quick-add-button');
          
          if (isQuickAddButton) {
            // Quick Add: Apenas reabilitar, conteúdo não foi alterado
            button.disabled = false;
            button.classList.remove('digital-product-in-cart');
          } else {
            // Botão normal: Restaurar HTML original
            if (button.dataset.originalHTML) {
              button.innerHTML = button.dataset.originalHTML;
              delete button.dataset.originalHTML;
            }
            
            // Reativar botão completamente
            button.disabled = false;
            button.style.opacity = '1';
            button.style.cursor = 'pointer';
            button.classList.remove('digital-product-in-cart');
          }
          
          // Liberar bloqueio de cliques se existir
          this.addToCartInProgress.delete(variantId);
          
          console.log('[DigitalProductsProtection] ✅ Botão reativado com sucesso');
        }
      }
      
      console.log('[DigitalProductsProtection] ✅ Atualização de botões concluída');
      
    } catch (error) {
      console.error('[DigitalProductsProtection] ❌ Erro ao atualizar botões:', error);
    }
  }

  /**
   * Mostra mensagem de erro para produtos digitais
   */
  showDigitalProductError(message) {
    console.log('[DigitalProductsProtection] 🚨 Mostrando erro:', message);
    
    // Tentar usar o sistema de notificação existente do tema
    if (window.showNotification) {
      window.showNotification(message, 'error');
      return;
    }
    
    // Tentar usar outros sistemas de notificação
    if (window.theme && window.theme.notification) {
      window.theme.notification(message, 'error');
      return;
    }
    
    // Fallback: criar notificação customizada
    this.createNotification(message, 'error');
  }
  
  /**
   * Cria notificação manual
   */
  createNotification(message, type = 'info') {
    // Remover notificações anteriores
    const existingNotifications = document.querySelectorAll('.jv-digital-product-notification');
    existingNotifications.forEach(n => n.remove());
    
    const notification = document.createElement('div');
    notification.className = `jv-digital-product-notification ${type}`;
    
    // Estilos inline
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: type === 'error' ? '#dc3545' : '#28a745',
      color: 'white',
      padding: '15px 20px',
      borderRadius: '8px',
      zIndex: '99999',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      maxWidth: '350px',
      minWidth: '300px',
      border: type === 'error' ? '2px solid #c82333' : '2px solid #1e7e34',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    });
    
    notification.innerHTML = `
      <div style="font-size: 18px;">⚠️</div>
      <div style="flex: 1; line-height: 1.4;">${message}</div>
      <button onclick="this.parentElement.remove()" style="background: none; border: none; color: white; font-size: 20px; cursor: pointer; padding: 0; width: 24px; height: 24px; border-radius: 50%; transition: background-color 0.2s;">×</button>
    `;
    
    // Adicionar animação CSS
    if (!document.getElementById('jv-digital-error-styles')) {
      const styles = document.createElement('style');
      styles.id = 'jv-digital-error-styles';
      styles.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(styles);
    }
    
    document.body.appendChild(notification);
    
    // Auto-remover após 5 segundos
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutRight 0.3s ease-out';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }
    }, 5000);
    
    // Animação de entrada
    notification.style.animation = 'slideInRight 0.3s ease-out';
    
    console.log('[DigitalProductsProtection] ✅ Notificação criada e exibida');
  }
}

// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DigitalProductsProtection();
  });
} else {
  new DigitalProductsProtection();
}

// Exportar para uso global se necessário
window.DigitalProductsProtection = DigitalProductsProtection;
